import { z } from 'zod';
import { profondeurDeclaree } from './conditions.js';

export interface SourceSimple {
  champ?: string;
  champs?: readonly (string | SourceSimple)[];
  separateur?: string;
  replis?: readonly SourceSimple[];
  affichage?: 'libelle';
  transformation?: 'initiales';
}
const nom = z.string().regex(/^(?:socle:)?[a-z][a-zA-Z0-9_]*$/);
export const sourceSimpleSchema: z.ZodType<SourceSimple, z.ZodTypeDef, unknown> = z.unknown().superRefine((value, ctx) => {
  if (!profondeurDeclaree(value)) ctx.addIssue({ code: 'custom', message: 'composition trop profonde' });
}).pipe(z.lazy(() => z.object({
  champ: nom.optional(), champs: z.array(z.union([nom, sourceSimpleSchema])).min(2).max(32).optional(),
  separateur: z.string().max(80).optional(), replis: z.array(sourceSimpleSchema).min(1).max(16).optional(),
  affichage: z.literal('libelle').optional(), transformation: z.literal('initiales').optional(),
}).strict().superRefine((source, ctx) => {
  if (Number(source.champ !== undefined) + Number(source.champs !== undefined) !== 1)
    ctx.addIssue({ code: 'custom', message: 'une sous-source porte champ ou champs' });
})));

export function champsDeSource(source: SourceSimple): string[] {
  return [ ...(source.champ ? [source.champ] : []),
    ...(source.champs ?? []).flatMap(p => typeof p === 'string' ? [p] : champsDeSource(p)),
    ...(source.replis ?? []).flatMap(champsDeSource) ];
}
export function initialesDuTexte(value: string): string {
  return value.trim().split(/\s+/).slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase();
}
export const estSourceTransformee = (source: SourceSimple): boolean => source.champs !== undefined
  || source.replis !== undefined || source.transformation !== undefined || source.affichage !== undefined;

/** La jointure et ses replis. Les lecteurs fournissent les libelles/references. */
export function evaluerSource<T>(source: SourceSimple, lire: (champ: string, libelle: boolean) => T,
  texte: (value: T) => string, joindre: (parties: string[]) => string): T | string {
  let value: T | string = source.champ !== undefined ? lire(source.champ, source.affichage === 'libelle')
    : (source.separateur === undefined ? joindre : (p: string[]) => p.filter(Boolean).join(source.separateur))(
      (source.champs ?? []).map(p => {
        const v = typeof p === 'string' ? lire(p, source.affichage === 'libelle') : evaluerSource(p, lire, texte, joindre);
        return (typeof v === 'string' ? v : texte(v)).trim();
      }));
  const versTexte = (v: T | string) => (typeof v === 'string' ? v : texte(v)).trim();
  if (versTexte(value) === '') for (const repli of source.replis ?? []) {
    value = evaluerSource(repli, lire, texte, joindre);
    if (versTexte(value) !== '') break;
  }
  return source.transformation === 'initiales' ? initialesDuTexte(versTexte(value))
    : estSourceTransformee(source) && typeof value === 'string' ? value.trim() : value;
}
